import { createGlobalState, createSharedComposable, useDebounceFn } from "@vueuse/core";
import { omit } from "lodash";
import { computed, readonly, ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { createOrderFromCart as _createOrderFromCart } from "@/core/api/graphql";
import { useAnalytics, useHistoricalEvents, useThemeContext } from "@/core/composables";
import { AddressType, ProductType, DynamicProperties } from "@/core/enums";
import {
  DEFAULT_NOTIFICATION_DURATION,
  COC_NUMBER_PROP,
  VAT_NUMBER_PROP,
  EXCLUDED_DELIVERY_DATES,
} from "@/core/constants";

import { globals } from "@/core/globals";
import { isEqualAddresses, Logger, getDynamicPropertyValue } from "@/core/utilities";
import { useUser, useUserAddresses, useUserCheckoutDefaults } from "@/shared/account";
import { useFullCart, EXTENDED_DEBOUNCE_IN_MS } from "@/shared/cart";
import { useOrganizationAddresses } from "@/shared/company";
import { useModal } from "@/shared/modal";
import { useNotifications } from "@/shared/notification";
import { PaymentMethodGroupType } from "@/shared/payment";
import { BOPIS_CODE } from "./useBopis";
import type {
  InputDynamicPropertyValueType,
  CartAddressType,
  CustomerOrderType,
  InputAddressType,
  InputPaymentType,
  MemberAddressType,
  PaymentMethodType,
  ShippingMethodType,
} from "@/core/api/graphql/types";
import type { AnyAddressType } from "@/core/types";
import InnoSelectAddressModal from "@/shared/checkout/components/innovadis/inno-select-address-modal.vue";
import moment from "moment";
import AddOrUpdateCompanyAddressModal from "@/shared/company/components/add-or-update-company-address-modal.vue";

const useGlobalCheckout = createGlobalState(() => {
  const loading = ref(false);
  const billingAddressEqualsShipping = ref(false);
  const placedOrder = shallowRef<CustomerOrderType | null>(null);

  const commentChanging = ref(false);
  const _comment = ref<string>();

  const purchaseOrderNumberChanging = ref(false);
  const _purchaseOrderNumber = ref<string>();

  function clearState() {
    _comment.value = undefined;
  }
  return {
    loading,
    billingAddressEqualsShipping,
    placedOrder,
    commentChanging,
    _comment,
    purchaseOrderNumberChanging,
    _purchaseOrderNumber,
    clearState,
  };
});

export function _useCheckout() {
  const { analytics } = useAnalytics();
  const { t } = useI18n();
  const notifications = useNotifications();
  const { openModal, closeModal } = useModal();
  const router = useRouter();
  const { user, isAuthenticated, isCorporateMember, organization: organizationFromUser } = useUser();
  const { getUserCheckoutDefaults } = useUserCheckoutDefaults();
  const {
    addresses: personalAddresses,
    fetchAddresses: fetchPersonalAddresses,
    addOrUpdateAddresses: addOrUpdatePersonalAddresses,
  } = useUserAddresses();
  const {
    addresses: organizationsAddresses,
    fetchAddresses: fetchOrganizationAddresses,
    addOrUpdateAddresses: addOrUpdateOrganizationAddresses,
  } = useOrganizationAddresses(user.value.contact?.organizationId || "");
  const {
    refetch: refetchCart,
    cart,
    selectedLineItems,
    selectCartItems,
    shipment,
    payment,
    availableShippingMethods,
    availablePaymentMethods,
    hasValidationErrors,
    allItemsAreDigital,
    updateShipment,
    removeShipment,
    updatePayment,
    changeComment,
    updatePurchaseOrderNumber,
    updateCartDynamicProperties,
  } = useFullCart();
  const {
    loading,
    billingAddressEqualsShipping,
    placedOrder,
    commentChanging,
    _comment,
    purchaseOrderNumberChanging,
    _purchaseOrderNumber,
    clearState: clearGlobalCheckoutState,
  } = useGlobalCheckout();
  const { themeContext } = useThemeContext();
  const { pushHistoricalEvent } = useHistoricalEvents();

  const deliveryAddress = computed(() => shipment.value?.deliveryAddress);
  const isShippingMethodBopis = computed(() => shipment.value?.shipmentMethodCode === BOPIS_CODE);

  const _organization = ref<string>();
  const _cocNumber = ref<string>();
  const _vatNumber = ref<string>();

  const billingAddress = computed(() =>
    !allItemsAreDigital.value &&
    !isShippingMethodBopis.value &&
    billingAddressEqualsShipping.value &&
    deliveryAddress.value
      ? deliveryAddress.value
      : payment.value?.billingAddress,
  );
  const shipmentMethod = computed(() =>
    availableShippingMethods.value.find(
      (item: any) => item.id === shipment.value?.shipmentMethodCode + "_" + shipment.value?.shipmentMethodOption,
    ),
  );
  const paymentMethod = computed(() =>
    availablePaymentMethods.value.find((item: any) => item.code === payment.value?.paymentGatewayCode),
  );

  const customerCocNumber = computed(() => {
    const userCocNumber = organizationFromUser.value?.dynamicProperties.find(
      (item: any) => item.name === COC_NUMBER_PROP,
    )?.value;
    if (userCocNumber != null && userCocNumber !== "") {
      return userCocNumber;
    }

    const cartCocNumber = cart.value?.dynamicProperties.find((item: any) => item.name === COC_NUMBER_PROP)?.value;
    if (cartCocNumber != null && cartCocNumber !== "") {
      return cartCocNumber;
    }

    return "";
  });

  const customerVatNumber = computed(() => {
    const userVatNumber = organizationFromUser.value?.dynamicProperties.find(
      (item: any) => item.name === VAT_NUMBER_PROP,
    )?.value;
    if (userVatNumber != null && userVatNumber !== "") {
      return userVatNumber;
    }

    const cartVatNumber = cart.value?.dynamicProperties.find((item: any) => item.name === VAT_NUMBER_PROP)?.value;
    if (cartVatNumber != null && cartVatNumber !== "") {
      return cartVatNumber;
    }

    return "";
  });

  const customerOrganizationName = computed<string>(() => {
    const userOrganizationName = organizationFromUser.value?.name;
    if (userOrganizationName != null && userOrganizationName !== "") {
      return userOrganizationName;
    }

    const billingOrganizationName = billingAddress.value?.organization;
    if (billingOrganizationName != null && billingOrganizationName !== "") {
      return billingOrganizationName;
    }

    return "";
  });

  const changeCommentDebounced = useDebounceFn(async (value: string) => {
    if (cart.value?.comment !== value) {
      await changeComment(value);
    }
    commentChanging.value = false;
  }, EXTENDED_DEBOUNCE_IN_MS);

  const updatePurchaseOrderNumberDebounced = useDebounceFn(async (value: string) => {
    if (cart.value?.purchaseOrderNumber !== value) {
      await updatePurchaseOrderNumber(value);
    }
    purchaseOrderNumberChanging.value = false;
  }, EXTENDED_DEBOUNCE_IN_MS);

  // Innovadis custom
  async function setDeliveryDate(method: string) {
    const formattedDate = moment(method).format("YYYY-MM-DD HH:mm:ss.SSSSSSS");

    await updateShipment({
      id: shipment.value?.id,
      dynamicProperties: [
        {
          name: "deliveryDate",
          value: method ? formattedDate : "",
        },
      ],
    });
  }

  const comment = computed({
    get: () => _comment.value ?? cart.value?.comment ?? "",
    set: (value: string) => {
      commentChanging.value = true;
      _comment.value = value;
      void changeCommentDebounced(value.trim());
    },
  });

  const purchaseOrderNumber = computed({
    get: () => _purchaseOrderNumber.value ?? cart.value?.purchaseOrderNumber ?? "",
    set: (value: string) => {
      purchaseOrderNumberChanging.value = true;
      _purchaseOrderNumber.value = value;
      void updatePurchaseOrderNumberDebounced(value);
    },
  });

  // Innovadis custom functionality
  function updateDynamicProperties(propName: string, propValue: string) {
    let dynamicProperties = (cart?.value?.dynamicProperties as InputDynamicPropertyValueType[]) || [];
    const newProperty = [{ name: propName, value: propValue }];

    dynamicProperties = dynamicProperties.map((existingObj) => {
      const updatedObject = newProperty.find((newObj) => newObj.name === existingObj.name);
      return updatedObject ? updatedObject : existingObj;
    });
    newProperty.forEach((newObj) => {
      if (!dynamicProperties.some((existingObj) => existingObj.name === newObj.name)) {
        dynamicProperties.push(newObj);
      }
    });

    return dynamicProperties;
  }

  // CUSTOM INNOVADIS
  async function handleCustomerValueSet() {
    const values = [
      { name: DynamicProperties.Coc, value: customerCocNumber.value, debouncedFn: changeCocNumberDebounced },
      { name: DynamicProperties.Vat, value: customerVatNumber.value, debouncedFn: changeVatNumberDebounced },
      {
        name: DynamicProperties.Organization,
        value: customerOrganizationName.value,
        debouncedFn: changeOrganizationDebounced,
      },
    ];

    await updateCartDynamicProperties(values);
  }

  // Innovadis custom function
  function addWorkingDays(_startDate: Date, numberOfDays: number) {
    const excludedDatesPropertyValues = themeContext.value.dynamicProperties.filter((item) => item.name && item.name === EXCLUDED_DELIVERY_DATES);
    const excludedDates = excludedDatesPropertyValues.map(x => x.value);

    const currentDate = new Date(_startDate.getTime());

    while (numberOfDays > 0) {
      currentDate.setDate(currentDate.getDate() + 1);
      const dayOfWeek = currentDate.getDay();
      const currentDateStr = currentDate.toISOString().split("T")[0];

      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !excludedDates.includes(currentDateStr)) {
        numberOfDays--;
      }
    }

    return currentDate;
  }

  // Innovadis custom functionality
  const changeOrganizationDebounced = useDebounceFn(async (value: string) => {
    if (
      !cart.value?.dynamicProperties.find(
        (item: any) => item.name === DynamicProperties.Organization && item.value === value,
      )
    ) {
      await updateCartDynamicProperties(updateDynamicProperties(DynamicProperties.Organization, value));
    }
    _organization.value = undefined;
  }, EXTENDED_DEBOUNCE_IN_MS);

  // Innovadis custom functionality
  const changeCocNumberDebounced = useDebounceFn(async (value: string) => {
    if (
      !cart.value?.dynamicProperties.find((item: any) => item.name === DynamicProperties.Coc && item.value === value) &&
      isValidCocNumber.value
    ) {
      await updateCartDynamicProperties(updateDynamicProperties(DynamicProperties.Coc, value));
    } else if (!isValidCocNumber.value) {
      return;
    }
  }, EXTENDED_DEBOUNCE_IN_MS);

  // Innovadis custom functionality
  const changeVatNumberDebounced = useDebounceFn(async (value: string) => {
    if (
      !cart.value?.dynamicProperties.find((item: any) => item.name === DynamicProperties.Vat && item.value === value) &&
      isValidVatNumber.value
    ) {
      await updateCartDynamicProperties(updateDynamicProperties(DynamicProperties.Vat, value));
    } else if (!isValidVatNumber.value) {
      return;
    }
  }, EXTENDED_DEBOUNCE_IN_MS);

  // Innovadis custom functionality
  const organization = computed({
    get: () =>
      (_organization.value ??
        (cart.value?.dynamicProperties.find((item) => item.name === DynamicProperties.Organization)
          ?.value as string)) ||
      "",
    set: async (value: string) => {
      _organization.value = value;
      await changeOrganizationDebounced(value);
    },
  });

  // Innovadis custom functionality
  const cocNumber = computed({
    get: () =>
      (_cocNumber.value ??
        cart.value?.dynamicProperties.find((item: any) => item.name === DynamicProperties.Coc)?.value) ||
      getDynamicPropertyValue(organizationFromUser.value?.dynamicProperties, DynamicProperties.Coc) ||
      "",
    set: async (value: string) => {
      _cocNumber.value = value;
      await changeCocNumberDebounced(value);
    },
  });

  // Innovadis custom functionality
  const isValidCocNumber = computed<boolean>(() => {
    const pattern = /^.{8}$/;

    //Registered accounts get coc/vat number validated during registration process, so here we can skipp known values
    const knownOrganizationCocValue = getDynamicPropertyValue(
      organizationFromUser.value?.dynamicProperties,
      DynamicProperties.Coc,
    );

    if (knownOrganizationCocValue != null && knownOrganizationCocValue !== "") {
      return true;
    }

    if (_cocNumber.value != null && _cocNumber.value != "") {
      return pattern.test(_cocNumber?.value || "");
    }

    return pattern.test(cocNumber?.value || "");
  });

  // Innovadis custom functionality
  const vatNumber = computed({
    get: () =>
      (_vatNumber.value ??
        cart.value?.dynamicProperties.find((item: any) => item.name === DynamicProperties.Vat)?.value) ||
      getDynamicPropertyValue(organizationFromUser.value?.dynamicProperties, DynamicProperties.Vat) ||
      "",
    set: async (value: string) => {
      _vatNumber.value = value;
      await changeVatNumberDebounced(value);
    },
  });

  // Innovadis custom functionality
  const isValidVatNumber = computed<boolean>(() => {
    const pattern = /^BE\d{10}$/;

    const knownOrganizationVatValue = getDynamicPropertyValue(
      organizationFromUser.value?.dynamicProperties,
      DynamicProperties.Vat,
    );

    if (knownOrganizationVatValue != null && knownOrganizationVatValue != "") {
      return true;
    }

    if (_vatNumber.value != null && _vatNumber.value != "") {
      return pattern.test(_vatNumber?.value || "");
    }

    return pattern.test(vatNumber?.value || "");
  });

  const isValidDeliveryAddress = computed<boolean>(() => !!shipment.value?.deliveryAddress);
  const isValidBillingAddress = computed<boolean>(
    () =>
      (!allItemsAreDigital.value && !isShippingMethodBopis.value && billingAddressEqualsShipping.value) ||
      !!payment.value?.billingAddress,
  );
  const isValidShipmentMethod = computed<boolean>(() => !!shipment.value?.shipmentMethodCode);
  const isValidPaymentMethod = computed<boolean>(() => !!payment.value?.paymentGatewayCode);
  const isValidShipment = computed<boolean>(() => isValidDeliveryAddress.value && isValidShipmentMethod.value);
  const isValidPayment = computed<boolean>(() => isValidBillingAddress.value && isValidPaymentMethod.value);
  const isValidCheckout = computed<boolean>(() =>
    allItemsAreDigital.value
      ? isValidPayment.value && !hasValidationErrors.value
      : isValidShipment.value && isValidPayment.value && !hasValidationErrors.value,
  );

  const selectedPaymentMethodGroupType = computed<string | undefined>(() => {
    const paymentMethodCode = placedOrder.value?.inPayments[0].paymentMethod?.code ?? payment.value?.paymentGatewayCode;
    return availablePaymentMethods.value.find((item: any) => item.code === paymentMethodCode)?.paymentMethodGroupType;
  });

  // Innovadis custom No need for authorization because you can't see cart page
  const canPayNow = computed<boolean>(
    () =>
      !!selectedPaymentMethodGroupType.value &&
      selectedPaymentMethodGroupType.value !== PaymentMethodGroupType[PaymentMethodGroupType.Manual],
  );

  const addresses = computed<AnyAddressType[]>(() => {
    const { firstName, lastName } = user.value.contact ?? {};

    return isCorporateMember.value
      ? organizationsAddresses.value.map((address) => ({ ...address, firstName, lastName }))
      : personalAddresses.value;
  });

  const isPurchaseOrderNumberEnabled = computed<boolean>(
    () =>
      !!selectedPaymentMethodGroupType.value &&
      selectedPaymentMethodGroupType.value === PaymentMethodGroupType[PaymentMethodGroupType.Manual],
  );

  const allOrderItemsAreDigital = computed(() =>
    placedOrder.value?.items?.every((item) => item.productType === ProductType.Digital),
  );

  function isExistAddress(address: AnyAddressType): boolean {
    return addresses.value.some((item) => isEqualAddresses(item, address));
  }

  async function setShippingMethod(method: ShippingMethodType): Promise<void> {
    await updateShipment({
      id: shipment.value?.id,
      price: method.price?.amount,
      shipmentMethodCode: method.code,
      shipmentMethodOption: method.optionName,
    });
  }

  async function setPaymentMethod(method: PaymentMethodType): Promise<void> {
    await updatePayment({
      id: payment.value?.id,
      paymentGatewayCode: method.code,
    });
  }

  watch(allItemsAreDigital, async (value, previousValue) => {
    // Update defaults if state changed not on initialization
    if (previousValue !== undefined && value !== undefined && previousValue !== value) {
      await setCheckoutDefaults();
    }
  });

  async function setCheckoutDefaults(): Promise<void> {
    const { shippingMethodId, paymentMethodCode } = getUserCheckoutDefaults();
    const defaultShippingMethod = availableShippingMethods.value.find((item: any) => item.id === shippingMethodId);
    const defaultPaymentMethod = availablePaymentMethods.value.find((item: any) => item.code === paymentMethodCode);

    if (allItemsAreDigital.value && shipment.value) {
      await removeShipment(shipment.value.id);
    }

    // Create at initialization to prevent duplication due to lack of id
    if (!allItemsAreDigital.value && !shipment.value?.shipmentMethodCode && !shipment.value?.shipmentMethodOption) {
      if (shippingMethodId && defaultShippingMethod) {
        await setShippingMethod(defaultShippingMethod);
      } else if (!shipment.value) {
        await updateShipment({});
      }
    }

    if (!payment.value?.paymentGatewayCode) {
      if (paymentMethodCode && defaultPaymentMethod) {
        await setPaymentMethod(defaultPaymentMethod);
      } else if (!payment.value) {
        await updatePayment({});
      }
    }
  }

  async function initialize(): Promise<void> {
    placedOrder.value = null;
    loading.value = true;

    await setCheckoutDefaults();

    void fetchAddresses();

    analytics("beginCheckout", { ...cart.value!, items: selectedLineItems.value });

    loading.value = false;
  }

  async function updateBillingOrDeliveryAddress(
    addressType: AddressType,
    inputAddress: InputAddressType,
  ): Promise<void> {
    if (
      addressType === AddressType.Billing &&
      (!payment.value?.billingAddress || !isEqualAddresses(payment.value?.billingAddress, inputAddress))
    ) {
      await updatePayment({
        id: payment.value?.id,
        billingAddress: inputAddress,
      });
    } else if (
      addressType === AddressType.Shipping &&
      (!shipment.value?.deliveryAddress || !isEqualAddresses(shipment.value?.deliveryAddress, inputAddress))
    ) {
      await updateShipment({
        id: shipment.value?.id,
        deliveryAddress: inputAddress,
      });
    }
  }

  function openAddOrUpdateAddressModal(
    addressType: AddressType,
    editableAddress?: MemberAddressType | CartAddressType,
  ): void {
    openModal({
      component: AddOrUpdateCompanyAddressModal,
      props: {
        address: editableAddress,
        addressType: addressType,

        async onResult(address: MemberAddressType) {
          closeModal();

          const inputAddress: InputAddressType = {
            ...omit(address, ["id", "isDefault", "description", "isFavorite"]),
            addressType,
          };

          await updateBillingOrDeliveryAddress(addressType, inputAddress);
        },
      },
    });
  }

  function getInnoFilteredAddresses(addresses: AnyAddressType[], addressType: AddressType) {
    if (globals.storeId === "HerasFR") {
      if (addressType === AddressType.Billing) {
        const billing = addresses.filter((a) => a.addressType === AddressType.Billing);
        return billing.length > 0 ? billing : addresses.filter((a) => a.addressType === AddressType.BillingAndShipping);
      }

      if (addressType === AddressType.Shipping) {
        return addresses.filter(
          (a) => a.addressType === AddressType.Shipping || a.addressType === AddressType.BillingAndShipping,
        );
      }

      return [];
    }

    // fallback for NL and BE
    if (addressType === AddressType.Billing) {
      return addresses.filter((a) => a.addressType !== AddressType.Shipping);
    }

    return addresses;
  }

  function openSelectAddressModal(addressType: AddressType): void {
    openModal({
      component: InnoSelectAddressModal,
      props: {
        // Innovadis custom
        addresses: getInnoFilteredAddresses(addresses.value, addressType),
        currentAddress:
          addressType === AddressType.Billing ? payment.value?.billingAddress : shipment.value?.deliveryAddress,
        isCorporateAddresses: isCorporateMember.value,
        addressType: addressType,

        async onResult(address?: MemberAddressType) {
          if (!address) {
            return;
          }

          const inputAddress: InputAddressType = {
            ...omit(address, ["id", "isDefault", "description", "isFavorite"]),
            addressType,
          };

          await updateBillingOrDeliveryAddress(addressType, inputAddress);
        },

        onAddNewAddress() {
          setTimeout(() => {
            openAddOrUpdateAddressModal(addressType);
          }, 500);
        },
      },
    });
  }

  async function fetchAddresses(): Promise<void> {
    if (!isAuthenticated.value) {
      return;
    }

    if (isCorporateMember.value) {
      await fetchOrganizationAddresses();
    } else {
      await fetchPersonalAddresses();
    }
  }

  function onDeliveryAddressChange(): void {
    addresses.value.length
      ? openSelectAddressModal(AddressType.Shipping)
      : openAddOrUpdateAddressModal(AddressType.Shipping, shipment.value?.deliveryAddress);
  }

  function onBillingAddressChange(): void {
    // Innovadis custom filter
    addresses.value.filter((address) => address.addressType !== AddressType.Shipping).length
      ? openSelectAddressModal(AddressType.Billing)
      : openAddOrUpdateAddressModal(AddressType.Billing, payment.value?.billingAddress);
  }

  function getNewAddresses(payload: {
    shippingAddress?: CartAddressType;
    billingAddress?: CartAddressType;
  }): MemberAddressType[] {
    const newAddresses: MemberAddressType[] = [];

    if (payload.shippingAddress && !isExistAddress(payload.shippingAddress)) {
      newAddresses.push({
        ...payload.shippingAddress,
        isDefault: false,
        isFavorite: false,
        // Innovadis custom: Only allowed to add shipping address
        addressType: AddressType.Shipping,
      });
    }
    if (
      payload.billingAddress &&
      !isExistAddress(payload.billingAddress) &&
      (!payload.shippingAddress || !isEqualAddresses(payload.shippingAddress, payload.billingAddress))
    ) {
      newAddresses.push({
        ...payload.billingAddress,
        isDefault: false,
        isFavorite: false,
        // Innovadis custom: Only allowed to add shipping address
        addressType: AddressType.Shipping,
      });
    }

    return newAddresses;
  }

  async function saveNewAddresses(payload: {
    shippingAddress?: CartAddressType;
    billingAddress?: CartAddressType;
  }): Promise<void> {
    const newAddresses: MemberAddressType[] = getNewAddresses(payload);

    if (isCorporateMember.value) {
      await addOrUpdateOrganizationAddresses(newAddresses);
    } else {
      await addOrUpdatePersonalAddresses(newAddresses);
    }
  }

  async function prepareOrderData(): Promise<void> {
    // Update payment with required properties
    const filledPayment: InputPaymentType = {
      id: payment.value!.id,
      amount: cart.value!.total.amount, // required
    };

    // Save shipping address as billing address
    if (!allItemsAreDigital.value && !isShippingMethodBopis.value && billingAddressEqualsShipping.value) {
      filledPayment.billingAddress = {
        ...omit(shipment.value!.deliveryAddress, ["id"]),
        addressType: AddressType.Billing,
      };
    }

    await updatePayment(filledPayment);

    // Reset purchase order number if it is unavailable
    if (!isPurchaseOrderNumberEnabled.value && purchaseOrderNumber.value) {
      await updatePurchaseOrderNumber("");
    }

    // Parallel saving of new addresses in account. Before cleaning shopping cart
    if (isAuthenticated.value) {
      void saveNewAddresses({
        shippingAddress:
          !allItemsAreDigital.value && !isShippingMethodBopis.value ? shipment.value!.deliveryAddress : undefined,
        billingAddress: payment.value!.billingAddress,
      });
    }
  }

  async function createOrderFromCart(): Promise<CustomerOrderType | null> {
    loading.value = true;

    await prepareOrderData();

    try {
      // TODO remove as CustomerOrderType. Infer it from API
      placedOrder.value = (await _createOrderFromCart(cart.value!.id)) as CustomerOrderType;
    } catch (e) {
      Logger.error(`${useCheckout.name}.${createOrderFromCart.name}`, e);
    }

    if (placedOrder.value) {
      await refetchCart();

      if (themeContext.value?.storeSettings?.defaultSelectedForCheckout && cart.value?.items.length) {
        selectCartItems(cart.value.items.map((item) => item.id));
      }

      clearState();

      analytics("placeOrder", placedOrder.value);
      void pushHistoricalEvent({
        eventType: "placeOrder",
        sessionId: placedOrder.value.id,
        productIds: placedOrder.value.items?.map((item) => item.productId),
        storeId: globals.storeId,
      });

      await router.replace({ name: canPayNow.value ? "CheckoutPayment" : "CheckoutCompleted" });
    } else {
      notifications.error({
        text: t("common.messages.creating_order_error"),
        duration: DEFAULT_NOTIFICATION_DURATION,
        single: true,
      });
    }

    loading.value = false;

    return placedOrder.value;
  }

  function clearState() {
    clearGlobalCheckoutState();
  }

  return {
    deliveryAddress,
    shipmentMethod,
    billingAddress,
    paymentMethod,
    comment,
    billingAddressEqualsShipping,
    purchaseOrderNumber,
    isValidDeliveryAddress,
    isValidBillingAddress,
    isValidShipmentMethod,
    isValidPaymentMethod,
    isValidShipment,
    isValidPayment,
    isValidCheckout,
    isPurchaseOrderNumberEnabled,
    selectedPaymentMethodGroupType,
    canPayNow,
    initialize,
    onDeliveryAddressChange,
    onBillingAddressChange,
    setShippingMethod,
    setPaymentMethod,
    createOrderFromCart,
    setDeliveryDate,
    loading: readonly(loading),
    changing: computed(() => commentChanging.value || purchaseOrderNumberChanging.value),
    placedOrder: computed(() => placedOrder.value),
    allOrderItemsAreDigital,

    // Innovadis custom
    organization,
    cocNumber,
    vatNumber,
    isValidCocNumber,
    handleCustomerValueSet,
    isValidVatNumber,
    addWorkingDays,
  };
}

export const useCheckout = createSharedComposable(_useCheckout);
